import {
  FormikSwitchField,
  FormikSwitchFieldProps,
  SwitchField,
  SwitchWithFieldLayoutProps
} from './field/SwitchField';
import { FormikTagsField, FormikTagsFieldProps } from './field/TagsField';
import { FormikRadioField, FormikRadioFieldProps } from './field/RadioField';
import {
  FormikCheckboxField,
  CheckboxField,
  CheckboxFieldProps,
  FormikCheckboxFieldProps
} from './field/CheckboxField';
import { FormikInputField, FormikInputFieldProps } from './field/InputField';
import {
  FormikLabelField,
  FormikLabelFieldProps,
  LabelField,
  LabelFieldProps
} from './field/LabelField';
import {
  FormikInputGroupField,
  FormikInputGroupFieldProps
} from './field/InputGroupField';
import { FormikUrlInputField } from './field/UrlInputField';
import { FormikTextAreaField } from './field/TextAreaField';
import {
  FormikSelectField,
  FormikSelectFieldProps,
  SelectField,
  SelectFieldProps
} from './field/SelectField';
import {
  FormikDatePickerField,
  FormikDatePickerFieldProps
} from './field/DatePickerField';
import {
  FormikDateRangePickerField,
  FormikDateRangePickerFieldProps
} from './field/DateRangePickerField';
import {
  FormikCustomField,
  CustomField,
  CustomFieldProps,
  FormikCustomFieldProps
} from './field/CustomField';
import {
  FormikFileInputField,
  FormikFileInputFieldProps
} from './field/FileInputField';
import {
  FormikMultiImageInputField,
  FormikMultiImageInputFieldProps
} from './field/MultiImageInputField';
import {
  FormikMultiFileInputField,
  FormikMultiFileInputFieldProps
} from './field/MultiFileInputField';
import { FieldLayoutProps } from 'hoc/withFieldLayout';
import { ReactNode } from 'react';
import {
  FormikConfigInputField,
  FormikConfigInputFieldProps
} from './field/FormikConfigInputField';

export interface FormField {
  component: (props: any) => ReactNode;
  props: any;
  hide: boolean;
}

export interface FormSection {
  title?: string;
  withShadow?: boolean;
  basicFields: FormField[];
  advancedFields?: FormField[];
  hintModal?: any;
}

export class FormConfig {
  sections: FormSection[];

  static Builder = class {
    sections: any[] = [];

    addSection(section: FormSection) {
      this.sections.push(section);
      return this;
    }

    build() {
      return new FormConfig(this.sections);
    }
  };

  static SectionBuilder = class {
    title?: string;
    withBottomShadow?: boolean;
    basicFields: FormField[];
    advancedFields?: FormField[];
    hintModal?: any;

    constructor(fields: FormField[]) {
      this.basicFields = fields;
    }

    withTitle(title: string) {
      this.title = title;
      return this;
    }

    addAdvancedFields(fields: FormField[]) {
      this.advancedFields = fields;
      return this;
    }

    withHintModal(hintModal: any) {
      this.hintModal = hintModal;
      return this;
    }

    withShadow(withShadow: boolean) {
      this.withBottomShadow = withShadow;
      return this;
    }

    build(): FormSection {
      return {
        title: this.title,
        withShadow: this.withBottomShadow,
        hintModal: this.hintModal,
        basicFields: this.basicFields,
        advancedFields: this.advancedFields
      };
    }
  };

  static FieldsBuilder = class {
    fields: FormField[];
    constructor(basicFields?: FormField[]) {
      this.fields = basicFields ? [...basicFields] : [];
    }

    addField = <T extends FieldLayoutProps>(
      component,
      props: T,
      hide: boolean
    ) => {
      this.fields.push({
        component,
        props,
        hide
      });
    };

    addFields = (fields: FormField[]) => {
      this.fields = [...this.fields, ...fields];
    };

    removeField = (name: string) => {
      this.fields = this.fields.filter((field) => field.props.name !== name);
    };

    addFormikCheckbox(props: FormikCheckboxFieldProps, hide = false) {
      this.addField(FormikCheckboxField, props, hide);
      return this;
    }

    addFormikSelect(props: FormikSelectFieldProps, hide = false) {
      this.addField(FormikSelectField, props, hide);
      return this;
    }

    addFormikInput(props: FormikInputFieldProps, hide = false) {
      this.addField(FormikInputField, props, hide);
      return this;
    }

    addFormikTextarea(props: FormikInputFieldProps, hide = false) {
      this.addField(FormikTextAreaField, props, hide);
      return this;
    }

    addFormikLabel(props: FormikLabelFieldProps, hide = false) {
      this.addField(FormikLabelField, props, hide);
      return this;
    }

    addFormikSwitch(props: FormikSwitchFieldProps, hide = false) {
      this.addField(FormikSwitchField, props, hide);
      return this;
    }

    addFormikCustom<Values = any>(
      props: FormikCustomFieldProps<Values>,
      hide = false
    ) {
      this.addField(FormikCustomField, props, hide);
      return this;
    }

    addFormikInputGroup(props: FormikInputGroupFieldProps, hide = false) {
      this.addField(FormikInputGroupField, props, hide);
      return this;
    }

    addFormikDatePicker(props: FormikDatePickerFieldProps, hide = false) {
      this.addField(FormikDatePickerField, props, hide);
      return this;
    }

    addFormikDateRangePicker(
      props: FormikDateRangePickerFieldProps,
      hide = false
    ) {
      this.addField(FormikDateRangePickerField, props, hide);
      return this;
    }

    addFormikTags(props: FormikTagsFieldProps, hide = false) {
      this.addField(FormikTagsField, props, hide);
      return this;
    }

    addFormikFileInput(props: FormikFileInputFieldProps, hide = false) {
      this.addField(FormikFileInputField, props, hide);
      return this;
    }

    addFormikUrlInput(props: FormikInputFieldProps, hide = false) {
      this.addField(FormikUrlInputField, props, hide);
      return this;
    }

    addFormikMultiImageInput(
      props: FormikMultiImageInputFieldProps,
      hide = false
    ) {
      this.addField(FormikMultiImageInputField, props, hide);
      return this;
    }

    addFormikMultiFileInput(
      props: FormikMultiFileInputFieldProps,
      hide = false
    ) {
      this.addField(FormikMultiFileInputField, props, hide);
      return this;
    }

    addFormikRadio(props: FormikRadioFieldProps, hide = false) {
      this.addField(FormikRadioField, props, hide);
      return this;
    }

    addFormikConfigInput(props: FormikConfigInputFieldProps, hide = false) {
      this.addField(FormikConfigInputField, props, hide);
      return this;
    }

    addLabel(props: LabelFieldProps, hide = false) {
      this.addField(LabelField, props, hide);
      return this;
    }

    addCheckbox(props: CheckboxFieldProps, hide = false) {
      this.addField(CheckboxField, props, hide);
      return this;
    }

    addCustom(props: CustomFieldProps, hide = false) {
      this.addField(CustomField, props, hide);
      return this;
    }

    addSwitch(props: SwitchWithFieldLayoutProps, hide = false) {
      this.addField(SwitchField, props, hide);
      return this;
    }

    addSelect(props: SelectFieldProps, hide = false) {
      this.addField(SelectField, props, hide);
      return this;
    }

    build(): FormField[] {
      return this.fields;
    }
  };

  constructor(sections) {
    this.sections = sections;
  }
}
